import { pipe } from "fp-ts/function";
import { array, number } from "fp-ts";
import {
    bibPassAfterStartView,
    bibPassTable,
    gunshotsTable,
} from "../domain/timing";
import { pick } from "../utils/fp";
import * as t from "io-ts";
import { EventId, RaceId } from "../domain/event";
import { useQuery } from "../utils/supabase/useQuery";
import { column, columns } from "../utils/pg-ts/pg-ts";
import { Nullable } from "../utils/types";

const gunshotColumns = pipe(gunshotsTable.columns, pick("gunshot", "id"));
const gunshotCodec = t.type(gunshotColumns, "Gunshot");
export type Gunshot = t.TypeOf<typeof gunshotCodec>;

const bibPassColumns = pipe(
    bibPassAfterStartView.columns,
    pick("id", "time", "bib", "ignored", "race_id", "time_relative")
);
const bibPassCodec = t.type(bibPassColumns, "EventBibPass");
export type EventBibPass = t.TypeOf<typeof bibPassCodec>;

export const useEventBibPasses = (raceIds: Array<RaceId>) => {
    const shouldSkip = array.isEmpty(raceIds);

    return useQuery(
        pipe(
            raceIds,
            array.sort(number.Ord),
            (ids) => `event_bib_passes_${ids.join("_")}`
        ),
        (supabase) =>
            supabase
                .from(bibPassAfterStartView.name)
                .select(columns(bibPassColumns))
                .in(column(bibPassAfterStartView, "race_id"), raceIds)
                .order(column(bibPassAfterStartView, "time_relative"), {
                    ascending: true,
                }),
        t.array(bibPassCodec),
        {
            refreshInterval: 5000,
        },
        shouldSkip
    );
};

const rawBibPassColumns = pipe(
    bibPassTable.columns,
    pick("id", "time", "bib", "ignored")
);
const rawBibPassCodec = t.type(rawBibPassColumns, "RawBibPass");
export type RawBibPass = t.TypeOf<typeof rawBibPassCodec>;

export const useEventRawBibPasses = (eventId: EventId) => {
    return useQuery(
        `event_raw_bib_passes_${eventId}`,
        (supabase) =>
            supabase
                .from(bibPassTable.name)
                .select(columns(rawBibPassColumns))
                .eq(column(bibPassTable, "event_id"), eventId)
                .order(column(bibPassTable, "time"), { ascending: true }),
        t.array(rawBibPassCodec),
        {
            refreshInterval: 5000,
        }
    );
};

export const useEventGunShots = (eventId: Nullable<EventId>) => {
    const shouldSkip = eventId == null;

    return useQuery(
        `gunshots_${eventId}`,
        (supabase) =>
            supabase
                .from(gunshotsTable.name)
                .select(columns(gunshotColumns))
                .eq(column(gunshotsTable, "event_id"), eventId),
        t.array(gunshotCodec),
        {
            refreshInterval: 10000,
        },
        shouldSkip
    );
};
